import _ from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { managerAPI } from '@/shared/api';
import {
  ADD_NEW_EVENT_TYPE_RHYTHM_EVENTS_TAB_OPTIONS,
  KEY_CANCEL,
  KEY_RECOIL,
  LIST_EVENT_SKIP_ON_RUN_EVENT,
  LIST_RUN_EVENT,
  PREVENT_AFIB_OVERRIDE_EVENT_LIST,
  RHYTHM_EVENT_LIST_CAN_EDIT,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  minumum3BeatsType,
  typesPersistEvent,
} from '@/shared/config';
import { convertToMoment } from '@/shared/lib';
import { getEventDuration, getEventDurationInSecond } from '@/shared/lib/aiUtils';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  selectActiveTab,
  selectBeatChanges,
  selectControlModal,
  selectEventChanges,
  selectEventNews,
  selectPickedEndDate,
  selectPickedStartDate,
  useReportStore,
} from '@/entities/report';
import {
  ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE,
  ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE,
  ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE,
  ADD_EVENT_NO_BEAT_ERROR_MESSAGE,
  UPDATE_EVENT_NO_DATA_ERROR_MESSAGE,
  generateAddEventExistErrorMessage,
  getBeatCountAddUpdateEvents,
  getEventBothSideAddUpdateEvents,
  isCheckContinueData,
  ADD_EVENT_UNDER_3_BEATS_ERROR_MESSAGE,
  updateEventChangeAndNew,
} from '@/entities/event';
import { logError } from '@/shared/lib/apiHelpers';
import { getDataRange, isRangesOverlap } from '@/shared/lib/timeRange';
import type { AddNewEventProps } from './addNewEvent';

/**
 * Backs the add-new-event modal on the rhythm events tab, holding the date and time pickers, the chosen event type and the validation state. Validates the picked range, then routes to the AFib path or the generic path, both of which clamp the event to available ECG data, enforce the minimum beat and duration rules, reconcile overlaps against persisted and locally added events, and commit the result to the event-news and event-changes stores as one undoable group.
 *
 * @param props - ECG data map, timezone, study and profile identifiers, report range and the onCancel callback.
 * @returns An object carrying the form state plus the onChangeEvent, onChangeCheckbox, onChange, onClickCancel and onClickAdd handlers.
 */
const useAddNewEvent = ({
  onCancel,
  timezone,
  timezoneOffset,
  ecgDataMap,
  studyId,
  profileId,
  startReport,
  stopReport,
}: AddNewEventProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  /**
   * Reads the locally added events for the rhythm events tab without subscribing to them. Runs the selector against the report store's snapshot so the async add-event flows see events committed after render.
   *
   * @returns The locally added events for this tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the locally added events for the rhythm events tab. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new event-news list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the pending edits to existing events for the rhythm events tab without subscribing to them. Runs the selector against the report store's snapshot so the async add-event flows see edits committed after render.
   *
   * @returns The pending event changes for this tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending edits to existing events for the rhythm events tab. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new event-changes list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  /**
   * Reads the pending beat edits for the rhythm events tab without subscribing to them. Runs the selector against the report store's snapshot so the async add-event flows see edits committed after render.
   *
   * @returns The pending beat changes for this tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Writes the start of the range picked in the add-event modal. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new start date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Writes the end of the range picked in the add-event modal. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new end date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Records the grouped event snapshot the undo and redo stack replays. Forwards the value to the store action under this tab's key.
   *
   * @param v - The new event options, or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);

  const [state, setState] = useMergeState<{
    startDate: any;
    startTime: any;
    endDate: any;
    endTime: any;
    eventType: any;
    errorObject: any;
    isSaving: boolean;
    isUse100Afib?: boolean;
  }>({
    startDate: undefined,
    startTime: undefined,
    endDate: undefined,
    endTime: undefined,
    eventType: ADD_NEW_EVENT_TYPE_RHYTHM_EVENTS_TAB_OPTIONS[0],
    errorObject: {},
    isSaving: false,
  });

  /**
   * Stores the event type chosen in the add-event form's dropdown. Writes the selected option under the given field name.
   *
   * @param name - Form field being set.
   * @param option - The selected dropdown option, carrying the event type value.
   * @returns Nothing.
   */
  const onChangeEvent = (name: string, option: any) => {
    setState({ [name]: option });
  };

  /**
   * Toggles the whole-report checkbox on the add-event form. Stores the value and, when checked, widens the picked range to the report's start and stop.
   *
   * @param name - Form field being set.
   * @param value - The new checkbox value.
   * @returns Nothing.
   */
  const onChangeCheckbox = (name: string, value: any) => {
    setState({ [name]: value });
    if (value) {
      setPickedStartDate(startReport);
      setPickedEndDate(stopReport);
    }
  };

  /**
   * Stores a picked date or time on the add-event form. Writes the value under the given field name and clears any validation errors already showing.
   *
   * @param name - Form field being set, one of the start or end date and time fields.
   * @param time - The picked moment to store under that field.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any) => {
    setState({
      [name]: time,
      errorObject: {},
    });
  };

  /**
   * Closes the add-event modal. Clears the validation errors and the saving flag, then delegates to the onCancel prop, passing on whether the new-event dialog should reopen.
   *
   * @param isOpenNewEvent - True when the caller wants the new-event dialog reopened after closing.
   * @returns Nothing.
   */
  const onClickCancel = (isOpenNewEvent?: boolean) => {
    setState({
      errorObject: {},
      isSaving: false,
    });
    onCancel(isOpenNewEvent);
  };

  /**
   * Aborts an add-event attempt with a toast and inline field errors. Raises the message as an error toast, marks both startTime and endTime invalid on a clone of the current error object and clears the saving flag.
   *
   * @param toastMessage - Message shown in the error toast, empty by default.
   * @returns Nothing.
   */
  const handleSetError = (toastMessage = '') => {
    toastrError(toastMessage, 'Error');
    const clonedErrorObject = { ...state.errorObject };
    _.assign(clonedErrorObject, {
      startTime: 'Invalid value',
      endTime: 'Invalid value',
    });
    setState({
      errorObject: clonedErrorObject,
      isSaving: false,
    });
  };

  /**
   * Resolves how a candidate event overlaps the events already covering its range. Walks the combined events, skipping the pairs AFib may not override and the run-event exemptions, errors out when the candidate sits inside a same-type or different-type event, absorbs same-type intersections by widening the candidate, and collects the old events to delete or trim, trimming only where the remainder still holds enough beats or duration.
   *
   * @param combinedEvents - Persisted events over the range merged with the local changes and additions.
   * @param newEvent - The candidate event, whose start and stop may be widened in place.
   * @param localBeatChanges - Locally added or deleted beats counted when measuring a trimmed remainder.
   * @returns A promise resolving to an object carrying linkEvents, errorEvent and errorMessages.
   */
  const processEvent = async ({ combinedEvents, newEvent, localBeatChanges }: any) => {
    const newEventStart = new Date(newEvent.start).getTime();
    const newEventStop = new Date(newEvent.stop).getTime();
    //* Gán type delete cho những event được link với new event
    const linkEvents: any[] = [];
    let errorEvent;
    let errorMessages;
    const combinedEventsLength = combinedEvents.length;

    for (let i = 0; i < combinedEventsLength; i += 1) {
      const event = combinedEvents[i];
      const eventType = event.type === TYPE_BEAT_ENUM.DELETED ? event.oldType : event.type;
      if (
        PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(eventType) &&
        newEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB
      ) {
        continue;
      }
      if (
        PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(newEvent.type) &&
        eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
      ) {
        continue;
      }
      const eventStart = new Date(event.start).getTime();
      const eventStop = new Date(event.stop).getTime();
      if (isRangesOverlap(eventStart, eventStop, newEventStart, newEventStop)) {
        const isSameType = eventType === newEvent.type;
        const isNewEventContainOldEvent = eventStart >= newEventStart && eventStop <= newEventStop;
        const isNewEventInsideOldEvent = newEventStart >= eventStart && newEventStop <= eventStop;
        if (isSameType) {
          //* Mới chứa cũ cùng loại
          if (isNewEventContainOldEvent) {
            console.log('[rhythmEvents/addNewEvent]-OVERLAP-sametype-isNewEventContainOldEvent');
            if (
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              linkEvents.push(event);
            }
          } else if (isNewEventInsideOldEvent) {
            //* Mới trong cũ cùng loại
            console.log('[rhythmEvents/addNewEvent]-OVERLAP-sametype-isNewEventInsideOldEvent');
            errorEvent = event;
            break;
          } else {
            console.log('[rhythmEvents/addNewEvent]-OVERLAP-sametype-intersect', event);
            //* Giao nhau
            if (
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              linkEvents.push(event);
            } else if (event.isNew) {
              linkEvents.push(event);
            }
            //* Đổi start stop của event mới là hợp của cả 2 event
            newEvent.start = moment(Math.min(eventStart, newEventStart)).toISOString();
            newEvent.stop = moment(Math.max(eventStop, newEventStop)).toISOString();
          }
        } else if (
          (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(eventType) &&
            LIST_RUN_EVENT.includes(newEvent.type)) ||
          (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(newEvent.type) &&
            LIST_RUN_EVENT.includes(eventType))
        ) {
          //* VT/SVT không cần check overlap cho 1 số event type khác
          continue;
        } else if (
          (!isNewEventContainOldEvent &&
            newEvent.type === TYPE_RHYTHM_EVENT_ENUM.SVT &&
            eventType === TYPE_BEAT_EVENT_ENUM.SVE_RUN) ||
          (newEvent.type === TYPE_RHYTHM_EVENT_ENUM.VT && eventType === TYPE_BEAT_EVENT_ENUM.VE_RUN)
        ) {
          if (isNewEventInsideOldEvent) {
            errorEvent = event;
            break;
          }
          console.log('[rhythmEvents/addNewEvent]-OVERLAP-sametype-intersect', {
            newEvent,
            event,
            eventType,
          });
          //* Giao nhau
          linkEvents.push(event);
          //* Đổi start stop của event mới là hợp của cả 2 event
          newEvent.start = moment(Math.min(eventStart, newEventStart)).toISOString();
          newEvent.stop = moment(Math.max(eventStop, newEventStop)).toISOString();
        } else if (RHYTHM_EVENT_LIST_CAN_EDIT.includes(eventType)) {
          //* Mới chứa cũ khác loại
          if (isNewEventContainOldEvent) {
            console.log('[rhythmEvents/addNewEvent]-OVERLAP-difftype-isNewEventContainOldEvent');
            if (
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              linkEvents.push(event);
            }
          } else if (isNewEventInsideOldEvent) {
            //* Mới trong cũ khác loại
            console.log('[rhythmEvents/addNewEvent]-OVERLAP-difftype-isNewEventInsideOldEvent');
            if (
              newEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              errorMessages = ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE;
              break;
            } else if (
              newEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT &&
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
            ) {
              errorMessages = ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE;
              break;
            }
            errorEvent = event;
            break;
          } else {
            if (
              eventType !== TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              continue;
            }
            console.log('[rhythmEvents/addNewEvent]-OVERLAP-difftype-intersect');
            let currentEventStart = event.start;
            let currentEventStop = event.stop;
            //* Giao nhau khác loại
            //* nếu artifact event sau khi cắt chỉ còn 1 beat => xét duration nếu lớn hơn 10s thì cắt được, nếu nhỏ hơn 10s thì delete artifact event
            //* nếu afib,vt,svt,1st,2nd,3rd event sau khi cắt chỉ còn 1 beat => xóa luôn
            //* nếu tachy,brady,pause event sau khi cắt chỉ còn 1 beat => xoá luôn.
            if (eventStart < newEventStart && newEventStart < eventStop) {
              currentEventStop = newEvent.start;
            }
            if (eventStart < newEventStop && newEventStop < eventStop) {
              currentEventStart = newEvent.stop;
            }

            const { mergeBeatCount } = await getBeatCountAddUpdateEvents({
              start: currentEventStart,
              stop: currentEventStop,
              samplingFrequency: ecgDataMap.samplingFrequency,
              localBeatChanges,
              ecgDataMap,
            });
            const newDuration = getEventDuration(currentEventStart, currentEventStop);
            const newDurationInSec = getEventDurationInSecond(currentEventStart, currentEventStop);
            //* Không có beat -> xóa event
            if (eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
              if (newDurationInSec >= 5) {
                linkEvents.push({
                  ...event,
                  start: currentEventStart,
                  stop: currentEventStop,
                  duration: newDuration,
                  canChangeDuration: true,
                });
              } else {
                linkEvents.push(event);
              }
            } else if (mergeBeatCount === 1) {
              // pre-existing: getEventDuration can return a formatted string (durations >= 60s), compared here as if always a number.
              if ((newDuration as any) >= 10 && eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
                //* Tạo được
                linkEvents.push({
                  ...event,
                  start: currentEventStart,
                  stop: currentEventStop,
                  duration: newDuration,
                  canChangeDuration: true,
                });
              } else {
                //* Xóa
                linkEvents.push(event);
              }
            } else if (mergeBeatCount === 0) {
              //* Xóa
              linkEvents.push(event);
            } else {
              //* Mảng beat lớn hơn 1
              //* Tạo được
              linkEvents.push({
                ...event,
                start: currentEventStart,
                stop: currentEventStop,
                duration: newDuration,
                canChangeDuration: true,
              });
            }
          }
        }
      }
    }

    return { linkEvents, errorEvent, errorMessages };
  };

  /**
   * Adds a non-AFib rhythm event, reconciling it against everything already covering its range. Clamps the range to the available ECG data, rejects gaps and ranges holding fewer than two beats or fewer than three for the run types, snaps the bounds onto real beats, fetches the surrounding persisted events merged with local changes, resolves the overlaps, then commits the new event together with the linked deletions and resizes as one undoable group.
   *
   * @param newEvent - The candidate event built from the picked range, type and channel.
   * @returns A promise resolving once the event is committed and the modal closes, or once an error toast has been shown.
   */
  const handleAddNewEvent = async (newEvent: any) => {
    const momentStart = moment(newEvent.start);
    const momentStop = moment(newEvent.stop);
    const dataRange = getDataRange(ecgDataMap, momentStart, momentStop);
    if (_.isEmpty(dataRange)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return;
    }
    const startData = dataRange[0].start;
    const stopData = dataRange[dataRange.length - 1].stop;
    if (startData > momentStart) {
      newEvent.start = new Date(startData).toISOString();
    }
    if (stopData < momentStop) {
      newEvent.stop = new Date(stopData).toISOString();
    }
    //* Được add những event: Artifact, Afib, Tachy, Brady, Pause, VT, SVT, AVB2, AVB3
    if (!isCheckContinueData(dataRange, newEvent)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return;
    }
    // Qui định khi add new event
    // 2 thằng cùng loại:
    // Giao nhau + mới chứa cũ: th mới và cũ hợp thành 1. Lấy even mới và delete th cũ.Khi xóa th mới đi thì revert th cũ khỏi state Delete
    // Mới trong cũ: ko tạo dc th mới. Báo toast
    // 2 thằng khác loại
    // Giao nhau: nó sẽ tạo ra 2 cái event luôn, với th mới thì theo cái đã chọn, còn th cũ điểm start time của nó là end time th kia. Hoặc là end time của th cũ sẽ là start time của th mới.
    // Mới chứa cũ: th mới xóa cũ. Khi xóa th mới đi thì revert th cũ khỏi state Delete
    // Mới trong cũ: ko tạo dc th mới. Báo toast
    const localBeatChanges = getBeatChanges().filter(
      (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
    );
    const { mergeBeatCount, startBeat, stopBeat } = await getBeatCountAddUpdateEvents({
      start: newEvent.start,
      stop: newEvent.stop,
      samplingFrequency: ecgDataMap.samplingFrequency,
      localBeatChanges,
      ecgDataMap,
    });
    if (mergeBeatCount < 2) {
      handleSetError(ADD_EVENT_NO_BEAT_ERROR_MESSAGE);
      return;
    }
    if (mergeBeatCount < 3 && minumum3BeatsType.includes(newEvent.type)) {
      handleSetError(ADD_EVENT_UNDER_3_BEATS_ERROR_MESSAGE);
      return;
    }
    if (startBeat && stopBeat) {
      newEvent.start = moment(startBeat).toISOString();
      newEvent.stop = moment(stopBeat).toISOString();
    }

    // fetch event to check
    const startSearchDate = newEvent.start;
    const stopSearchDate = newEvent.stop;
    const typesPersistEventClone: any[] = _.cloneDeep(typesPersistEvent);
    if (newEvent.type === TYPE_RHYTHM_EVENT_ENUM.SVT) {
      typesPersistEventClone.push(TYPE_BEAT_EVENT_ENUM.SVE_RUN);
    }
    if (newEvent.type === TYPE_RHYTHM_EVENT_ENUM.VT) {
      typesPersistEventClone.push(TYPE_BEAT_EVENT_ENUM.VE_RUN);
    }
    const holterEventsFilter = {
      studyId,
      profileId,
      types: typesPersistEventClone,
      startSearchDate,
      stopSearchDate,
    };
    // const { events } = await fetchHolterEvents(holterEventsFilter, 0, false, KEY_CANCEL.API_MODAL, true);
    const dataFormat = await getEventBothSideAddUpdateEvents(holterEventsFilter);
    const combinedEvents = updateEventChangeAndNew(
      dataFormat,
      getEventChanges(),
      getEventNews(),
      holterEventsFilter.startSearchDate,
      holterEventsFilter.stopSearchDate,
      typesPersistEventClone,
      true,
    );
    const { linkEvents, errorEvent, errorMessages } = await processEvent({
      combinedEvents,
      newEvent,
      localBeatChanges,
    });
    if (errorEvent) {
      handleSetError(generateAddEventExistErrorMessage(errorEvent, timezoneOffset));
      return;
    }
    if (errorMessages) {
      handleSetError(errorMessages);
      return;
    }
    const clonedEventChanges = _.cloneDeep(getEventChanges());
    const clonedEventNews = _.cloneDeep(getEventNews());
    newEvent.startTimeEpoch = moment(newEvent.start).startOf('minutes').valueOf();
    newEvent.duration = getEventDuration(newEvent.start, newEvent.stop);
    console.log('[rhythmEvents/addNewEvent]-linkevents', linkEvents);
    if (linkEvents.length) {
      linkEvents.forEach((x) => {
        if (x.isNew) {
          _.remove(clonedEventNews, (eventNew) => eventNew.id === x.id);
        } else if (x.canChangeDuration) {
          const foundEventChanged = clonedEventChanges.find(
            (eventChange) => eventChange.idEvent === x.idEvent,
          );
          if (foundEventChanged) {
            _.assign(foundEventChanged, {
              start: x.start,
              stop: x.stop,
              duration: x.duration,
            });
          } else {
            delete x.canChangeDuration;
            clonedEventChanges.push(x);
          }
        } else {
          const foundEventChanged = clonedEventChanges.find(
            (eventChange) => eventChange.idEvent === x.idEvent,
          );
          if (foundEventChanged) {
            _.assign(foundEventChanged, { type: TYPE_BEAT_ENUM.DELETED, oldType: x.oldType });
          }
          // else {
          //   clonedEventChanges.push({
          //     ...x,
          //     type: TYPE_BEAT_ENUM.DELETED,
          //     oldType: x.oldType,
          //   });
          // }
        }
      });
      newEvent.linkEvents = linkEvents.filter((x) => !x.isNew);
      clonedEventNews.push(newEvent);
    } else {
      clonedEventNews.push(newEvent);
    }
    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: clonedEventNews,
      eventChangesState: clonedEventChanges,
    }));
    setEventChanges(clonedEventChanges);
    setEventNews(clonedEventNews);
    onClickCancel(true);
  };

  /**
   * Adds an AFib event, which carries its own minimum-duration rules. Clamps the range to the available ECG data, rejects anything under five seconds by wall clock or by beat spacing, rebuilds the event when the data is not continuous, then reconciles each candidate against the surrounding persisted and local events and commits the new events with their linked deletions and resizes as one undoable group.
   *
   * @param newEvent - The candidate AFib event built from the picked range and channel.
   * @returns A promise resolving once the AFib events are committed and the modal closes, or once an error toast has been shown.
   */
  const handleAddNewEventAfib = async (newEvent: any) => {
    const momentStart = moment(newEvent.start);
    const momentStop = moment(newEvent.stop);
    const dataRange = getDataRange(ecgDataMap, momentStart, momentStop);
    if (_.isEmpty(dataRange)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return;
    }
    // * Resize duration to fit data range (để event không mắc qua vùng no data)
    const startData = dataRange[0].start;
    const stopData = dataRange[dataRange.length - 1].stop;
    if (startData > momentStart) {
      newEvent.start = new Date(startData).toISOString();
    }
    if (stopData < momentStop) {
      newEvent.stop = new Date(stopData).toISOString();
    }
    //* Check duration > 5
    if (getEventDurationInSecond(newEvent.start, newEvent.stop) < 5) {
      handleSetError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE);
      return;
    }

    const { channels } = ecgDataMap;

    const localBeatChanges = getBeatChanges().filter(
      (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
    );
    //* Check 1 beat
    const { mergeBeatCount, diffBeatBySecond, startBeat, stopBeat } =
      await getBeatCountAddUpdateEvents({
        start: newEvent.start,
        stop: newEvent.stop,
        samplingFrequency: ecgDataMap.samplingFrequency,
        localBeatChanges,
        ecgDataMap,
      });
    if (mergeBeatCount < 2) {
      handleSetError(ADD_EVENT_NO_BEAT_ERROR_MESSAGE);
      return;
    }
    if (diffBeatBySecond < 5) {
      handleSetError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE);
      return;
    }
    if (startBeat && stopBeat) {
      newEvent.start = moment(startBeat).toISOString();
      newEvent.stop = moment(stopBeat).toISOString();
    }
    //
    const newEvents: any[] = [];
    if (isCheckContinueData(dataRange, newEvent)) {
      newEvents.push(newEvent);
    } else {
      newEvents.push({
        id: moment(newEvent.start).valueOf(),
        start: newEvent.start,
        stop: newEvent.stop,
        type: TYPE_RHYTHM_EVENT_ENUM.AFIB,
        channel: channels.length === 3 ? 2 : 1,
        isNew: true,
      });
    }
    // fetch event to check
    const startSearchDate = newEvents[0].start;
    const stopSearchDate = newEvents[newEvents.length - 1].stop;
    const holterEventsFilter = {
      studyId,
      profileId,
      types: typesPersistEvent,
      startSearchDate,
      stopSearchDate,
      isUse100Afib: state.isUse100Afib,
    };
    // const { events } = await fetchHolterEvents(holterEventsFilter, 0, false, KEY_CANCEL.API_MODAL, true);
    const dataFormat = await getEventBothSideAddUpdateEvents(holterEventsFilter);
    const combinedEvents = updateEventChangeAndNew(
      dataFormat,
      getEventChanges(),
      getEventNews(),
      holterEventsFilter.startSearchDate,
      holterEventsFilter.stopSearchDate,
      typesPersistEvent,
    );

    const clonedEventNews = _.cloneDeep(getEventNews());
    const clonedEventChanges = _.cloneDeep(getEventChanges());
    for (let i = 0; i < newEvents.length; i += 1) {
      const currentNewEvent = newEvents[i];
      const localBeatChanges = getBeatChanges().filter(
        (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
      );
      const { linkEvents, errorEvent, errorMessages } = await processEvent({
        combinedEvents,
        newEvent: currentNewEvent,
        localBeatChanges,
      });
      if (errorEvent) {
        handleSetError(generateAddEventExistErrorMessage(errorEvent, timezoneOffset));
        return;
      }
      if (errorMessages) {
        handleSetError(errorMessages);
        return;
      }
      currentNewEvent.startTimeEpoch = moment(currentNewEvent.start).startOf('minutes').valueOf();
      currentNewEvent.stopTimeEpoch = moment(currentNewEvent.stop).endOf('minutes').valueOf();
      currentNewEvent.duration = getEventDuration(currentNewEvent.start, currentNewEvent.stop);
      if (linkEvents.length) {
        linkEvents.forEach((x) => {
          if (x.isNew) {
            _.remove(clonedEventNews, (eventNew) => eventNew.id === x.id);
          } else if (x.canChangeDuration) {
            const foundEventChanged = clonedEventChanges.find(
              (eventChange) => eventChange.idEvent === x.idEvent,
            );
            if (foundEventChanged) {
              _.assign(foundEventChanged, {
                start: x.start,
                stop: x.stop,
                duration: x.duration,
              });
            } else {
              delete x.canChangeDuration;
              clonedEventChanges.push(x);
            }
          } else {
            // * Gán type delete cho những event được link với new event
            const foundEventChanged = clonedEventChanges.find(
              (eventChange) => eventChange.idEvent === x.idEvent,
            );
            if (foundEventChanged) {
              _.assign(foundEventChanged, { type: TYPE_BEAT_ENUM.DELETED, oldType: x.oldType });
            }
            // else {
            //   clonedEventChanges.push({
            //     ...x,
            //     type: TYPE_BEAT_ENUM.DELETED,
            //     oldType: x.oldType,
            //   });
            // }
          }
        });
        currentNewEvent.linkEvents = linkEvents.filter((x) => !x.isNew);
        clonedEventNews.push(currentNewEvent);
      } else {
        clonedEventNews.push(currentNewEvent);
      }
    }
    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: clonedEventNews,
      eventChangesState: clonedEventChanges,
    }));
    setEventChanges(clonedEventChanges);
    setEventNews(clonedEventNews);
    onClickCancel(true);
  };

  /**
   * Validates the add-event form and submits it. Marks every missing date or time field invalid, rejects a start later than the end, and otherwise builds the candidate event from the picked range and channel count and routes it to the AFib or the generic add path, toasting the failure and clearing the saving flag if either throws.
   *
   * @returns A promise resolving once the event has been added or the failure has been reported.
   */
  const onClickAdd = async () => {
    setState({ isSaving: true });
    const clonedErrorObject = { ...state.errorObject };
    if (!state.startDate) {
      _.assign(clonedErrorObject, { startDate: 'Invalid value' });
    } else {
      delete clonedErrorObject.startDate;
    }
    if (!state.endDate) {
      _.assign(clonedErrorObject, { endDate: 'Invalid value' });
    } else {
      delete clonedErrorObject.endDate;
    }
    if (!state.startTime) {
      _.assign(clonedErrorObject, { startTime: 'Invalid value' });
    } else {
      delete clonedErrorObject.startTime;
    }
    if (!state.endTime) {
      _.assign(clonedErrorObject, { endTime: 'Invalid value' });
    } else {
      delete clonedErrorObject.endTime;
    }
    if (state.startDate && state.startTime && state.endDate && state.endTime) {
      if (state.startDate.isAfter(state.endDate)) {
        _.assign(clonedErrorObject, {
          startDate: 'Invalid value',
          endDate: 'Invalid value',
          startTime: 'Invalid value',
          endTime: 'Invalid value',
        });
      } else {
        delete clonedErrorObject.startDate;
        delete clonedErrorObject.endDate;
        delete clonedErrorObject.startTime;
        delete clonedErrorObject.endTime;
      }
    }

    if (!_.isEmpty(clonedErrorObject)) {
      setState({ errorObject: clonedErrorObject, isSaving: false });
      return;
    }

    const { channels } = ecgDataMap;

    try {
      if (state.eventType.value === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
        const newEvent = {
          id: new Date(pickedStartDate).getTime(),
          start: pickedStartDate,
          stop: pickedEndDate,
          type: TYPE_RHYTHM_EVENT_ENUM.AFIB,
          channel: channels.length === 3 ? 2 : 1,
          isNew: true,
        };
        await handleAddNewEventAfib(newEvent);
      } else {
        const newEvent = {
          id: new Date(pickedStartDate).getTime(),
          start: pickedStartDate,
          stop: pickedEndDate,
          type: state.eventType.value,
          channel: channels.length === 3 ? 2 : 1,
          isNew: true,
        };
        await handleAddNewEvent(newEvent);
      }
    } catch (error) {
      logError('Failed to add new event: ', error);
      toastrError(error.message, 'Error');
      setState({ isSaving: false });
    }
  };

  useUpdateEffect(() => {
    let convertedStartDateMoment;
    let convertedEndDateMoment;
    if (pickedStartDate) {
      convertedStartDateMoment = convertToMoment(pickedStartDate, timezone);
    }
    if (pickedEndDate) {
      convertedEndDateMoment = convertToMoment(pickedEndDate, timezone);
    }
    const stateObj = {
      startDate: convertedStartDateMoment,
      startTime: convertedStartDateMoment,
      endDate: convertedEndDateMoment,
      endTime: convertedEndDateMoment,
      errorObject: {},
    };
    if (
      startReport !== pickedStartDate ||
      stopReport !== pickedEndDate ||
      state.eventType.value !== TYPE_RHYTHM_EVENT_ENUM.AFIB
    ) {
      _.assign(stateObj, { isUse100Afib: false });
    }
    if (
      pickedStartDate <= startReport &&
      pickedEndDate >= stopReport &&
      state.eventType.value === TYPE_RHYTHM_EVENT_ENUM.AFIB
    ) {
      _.assign(stateObj, { isUse100Afib: true });
    }
    setState(stateObj);
  }, [pickedStartDate, pickedEndDate, startReport, stopReport, state.eventType]);

  useEffect(() => {
    setState({
      eventType:
        _.find(ADD_NEW_EVENT_TYPE_RHYTHM_EVENTS_TAB_OPTIONS, (x) => x.value === activeButton) ||
        ADD_NEW_EVENT_TYPE_RHYTHM_EVENTS_TAB_OPTIONS[0],
    });
  }, [activeButton]);

  useEffect(() => {
    if (!controlModal.isShowAddNewEvent) {
      managerAPI.cancelStore(KEY_CANCEL.API_MODAL);
    }
  }, [controlModal.isShowAddNewEvent]);

  return {
    state,
    onChangeEvent,
    onChangeCheckbox,
    onChange,
    onClickCancel,
    onClickAdd,
  };
};

export default useAddNewEvent;
